import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, ArrowLeft, Coffee, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function VerificationEmailPage() {
  const { resendConfirmationEmail } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const emailFromState = location.state?.email || '';
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({
    type: '',
    message: ''
  });

  useEffect(() => {
    let redirectTimeout;
    let hasHandledVerification = false;

    const handleVerified = () => {
      if (hasHandledVerification) return;
      hasHandledVerification = true;

      setFeedback({
        type: 'success',
        message: 'Votre adresse email a bien été vérifiée. Redirection vers votre compte...'
      });

      redirectTimeout = setTimeout(() => {
        navigate('/mon-compte', { replace: true });
      }, 1200);
    };

    const handleStorage = (event) => {
      if (event.key === 'email_verified' && event.newValue) {
        handleVerified();
      }
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data && event.data.type === 'EMAIL_VERIFIED') {
        handleVerified();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('message', handleMessage);
      if (redirectTimeout) clearTimeout(redirectTimeout);
    };
  }, [navigate]);

  const handleResendEmail = async () => {
    if (!emailFromState) {
      setFeedback({
        type: 'error',
        message: "Impossible de renvoyer l'email de confirmation : adresse email introuvable."
      });
      return;
    }

    setIsLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      const result = await resendConfirmationEmail(emailFromState);

      if (!result.success) {
        setFeedback({
          type: 'error',
          message: result.error || "Impossible de renvoyer l'email de confirmation."
        });
        return;
      }

      setFeedback({
        type: 'success',
        message: 'Un nouvel email de confirmation vient de vous être envoyé.'
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: "Une erreur est survenue lors de l'envoi."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16">
      <div className="w-full max-w-2xl px-6">
        <div className="bg-white border border-border-subtle overflow-hidden">
          <div className="bg-warm-secondary px-8 py-10 text-center border-b border-border-subtle">
            <Link to="/" className="inline-flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-full bg-latte-gold/10 flex items-center justify-center mr-3">
                <Coffee className="w-7 h-7 text-latte-gold" />
              </div>
              <span className="font-serif text-2xl font-semibold text-espresso">
                Léger&apos;s <span className="text-latte-gold">Coffee</span>
              </span>
            </Link>

            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-latte-gold/10">
              <Mail className="h-10 w-10 text-latte-gold" />
            </div>

            <h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold mb-3">
              Vérifiez votre adresse email
            </h1>

            <p className="text-soft-charcoal-secondary max-w-xl mx-auto leading-relaxed">
              Votre compte a bien été créé. Pour activer votre espace client, vous devez cliquer
              sur le lien de confirmation envoyé par email.
            </p>
          </div>

          <div className="px-8 py-8 lg:px-10">
            <div className="bg-warm-secondary border border-border-subtle p-5 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-latte-gold mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-espresso mb-1">Que faire maintenant ?</p>
                  <p className="text-sm text-soft-charcoal-secondary leading-relaxed">
                    Ouvrez votre boîte mail, recherchez le message de confirmation, puis cliquez
                    sur le lien d’activation avant de revenir sur le site.
                  </p>
                </div>
              </div>
            </div>

            {emailFromState && (
              <div className="mb-6 rounded-md border border-border-subtle bg-white px-4 py-4">
                <p className="text-sm text-soft-charcoal-secondary mb-1">Email concerné</p>
                <p className="font-medium text-espresso break-all">{emailFromState}</p>
              </div>
            )}

            <div className="space-y-3">
              <Button
                type="button"
                onClick={handleResendEmail}
                disabled={isLoading || !emailFromState}
                className="w-full btn-primary h-14 text-base"
              >
                {isLoading ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renvoyer l’email de confirmation
                  </>
                )}
              </Button>

              <Button asChild type="button" variant="ghost" className="w-full btn-secondary h-14 text-base">
                <Link to="/connexion">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à la connexion
                </Link>
              </Button>
            </div>

            {feedback.message && (
              <div
                className={`mt-6 rounded-md px-4 py-3 text-sm ${
                  feedback.type === 'success'
                    ? 'border border-green-200 bg-green-50 text-green-700'
                    : 'border border-red-200 bg-red-50 text-red-700'
                }`}
              >
                {feedback.message}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-border-subtle">
              <p className="text-sm text-soft-charcoal-secondary leading-relaxed">
                Vous ne trouvez pas le message ? Pensez à vérifier vos courriers indésirables
                ou l’onglet promotions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}